import type { KeyboardEvent, MutableRefObject } from 'react'
import type { SelectInstance } from 'react-select'

const FOCUSABLE_SELECTOR =
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'

function getFocusableElements(): HTMLElement[] {
    return Array.from(
        document.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)
    ).filter(
        (el) => el.getClientRects().length > 0 && !el.hasAttribute('disabled')
    )
}

function focusAdjacentElement(from: HTMLElement, backward: boolean) {
    const focusable = getFocusableElements()
    const index = focusable.indexOf(from)
    if (index === -1) return
    focusable[index + (backward ? -1 : 1)]?.focus()
}

export function createSelectKeyDownHandler<O>(
    ref: MutableRefObject<SelectInstance<O> | null>
) {
    return (event: KeyboardEvent<HTMLDivElement>) => {
        const instance = ref.current
        if (!instance) return

        if (event.key === 'Tab') {
            if (!instance.props.menuIsOpen) return
            const active = document.activeElement as HTMLElement | null
            event.preventDefault()
            instance.blurInput()
            if (active) focusAdjacentElement(active, event.shiftKey)
            return
        }

        if (event.key !== 'Enter' || event.keyCode === 229) return
        if (instance.props.menuIsOpen) return
        event.preventDefault()
        instance.openMenu('first')
    }
}

export const ariaLiveFr = {
    guidance: ({
        context,
        isInitialFocus,
    }: {
        context: 'menu' | 'input' | 'value'
        isInitialFocus: boolean
    }): string => {
        if (context === 'input' && isInitialFocus) {
            return 'Appuyez sur Entrée ou Espace pour ouvrir la liste.'
        }
        if (context === 'menu') {
            return 'Utilisez les flèches pour choisir, Entrée pour valider, Échap pour fermer.'
        }
        return ''
    },
    onChange: ({ action, label }: { action: string; label: string }): string =>
        action === 'select-option' ? `${label} sélectionné.` : '',
    onFocus: <Option>({
        context,
        label,
        focused,
        options,
    }: {
        context: 'menu' | 'value'
        label: string
        focused: Option
        options: ReadonlyArray<Option>
    }): string => {
        if (context !== 'menu') return ''
        return `${label}, ${options.indexOf(focused) + 1} sur ${
            options.length
        }.`
    },
    onFilter: (): string => '',
}

export const screenReaderStatusFr = ({ count }: { count: number }): string =>
    `${count} résultat${count > 1 ? 's' : ''}.`
